'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { LexicalContentRendererProps, ParagraphCommentUi } from '../types/lexical.types';

type UseParagraphCommentUiParams = Pick<
  LexicalContentRendererProps,
  'selectedParagraphId' | 'getParagraphCommentCount' | 'onCommentIconClick' | 'content'
>;

export function useParagraphCommentUi({
  selectedParagraphId,
  getParagraphCommentCount,
  onCommentIconClick,
  content,
}: UseParagraphCommentUiParams) {
  const [revealedParagraphId, setRevealedParagraphId] = useState<string | null>(null);
  const [prevContent, setPrevContent] = useState(content);

  // Reset reveal when chapter content changes (avoid setState-in-effect)
  if (content !== prevContent) {
    setPrevContent(content);
    setRevealedParagraphId(null);
  }

  const onRevealParagraph = useCallback((paragraphId: string) => {
    setRevealedParagraphId(paragraphId);
  }, []);

  useEffect(() => {
    if (!revealedParagraphId) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(`[data-comment-trigger="${revealedParagraphId}"]`)) return;
      setRevealedParagraphId(null);
    };

    // next tick so the revealing tap itself doesn't immediately dismiss
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [revealedParagraphId]);

  const paragraphCommentUi = useMemo<ParagraphCommentUi>(
    () => ({
      selectedParagraphId,
      revealedParagraphId,
      getParagraphCommentCount,
      onRevealParagraph,
      onCommentIconClick,
    }),
    [
      selectedParagraphId,
      revealedParagraphId,
      getParagraphCommentCount,
      onRevealParagraph,
      onCommentIconClick,
    ],
  );

  return { paragraphCommentUi };
}
